import { RootState } from "@/redux/store";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CATEGORY, DAYS, DEPS, depsTypes, ID, PLANNED_END_DATE, PLANNED_START_DATE, QUALITY_CHECK, SAFETY_CHECK, SUB_CATEGORY, TASK_NAME } from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import { routes } from "@/routes/utils";
import { getRequiredFields, removeEmptyFields } from "@/lib/utils";
import { useAddTaskMutation, useDeleteDependencyMutation, useGetDependencyList, useGetTaskById, useGetTaskCategoriesByProject, useGetTaskSubCategoriesByCategoryId, useUpdateTaskMutation } from "@/services";
import { setDependencyData, setTaskData } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { formValues, schema } from "./validations";
import { toast } from "@/components";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui";
import { MoreVertical, Edit, Trash } from "lucide-react";
import { useAddEditTaskContext } from "../AddEditTaskContext";

const useTaskPlanning = () => {
  const {taskData, dependencyData} = useSelector((state: RootState) => state.currentData);
  const queryClient = useQueryClient();
  const {id, planId, action, planAction} = useParams();
  const isView = action == routes.view || planAction == routes.view;
  const dispatch = useDispatch();
  const {isTaskExist, taskList} = useAddEditTaskContext();
  const [openAddEditDepsDialog, setOpenAddEditDepsDialog] = useState<boolean>(false);
  const [openDeleteDepsDialog, setOpenDeleteDepsDialog] = useState<boolean>(false);
  const isEditDeps = useRef(false);
  const deleteDepsIndex = useRef(-1);
  const form = useForm<formValues>({
    defaultValues: {
      [ID]: "",
      [CATEGORY]: "",
      [SUB_CATEGORY]: "",
      [TASK_NAME]: "",
      [PLANNED_START_DATE]: "",
      [PLANNED_END_DATE]: "",
      [DAYS]: "",
      [SAFETY_CHECK]: false,
      [QUALITY_CHECK]: false,
      [DEPS]: []
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const { data: categoryList, isLoading: isCategoryListLoading } =
    useGetTaskCategoriesByProject({ projectId: id as string });

  const { data: subCategoryList, isLoading: isSubCategoryListLoading, refetch: refetchTaskSubCategories, isRefetching: isRefetchingSubCategories } =
    useGetTaskSubCategoriesByCategoryId({ categoryId: form.watch(CATEGORY) as string, queryParams: {
      enabled: false
    } });
    
  useEffect(() => {
    if (form.watch(CATEGORY)) {
      refetchTaskSubCategories()
    }
  }, [form.watch(CATEGORY)]);

  const {data: task, isLoading: isLoadingTask, refetch: refetchTask, isRefetching: isTaskRefetching} = useGetTaskById({
    taskId: taskData?.taskId,
    projectId: id as string,
    planId: planId as string,
    queryParams: {
      enabled: !!taskData?.taskId
    }
  });

  useEffect(() => {
    if (taskData) {
      refetchTask();
    } else {
      onClear();
    }
  }, [taskData])

  useEffect(() => {
    if (task?.data) {
      form.reset({
        ...form.watch(),
        [ID]: task?.data?.[ID]?.toString() || "",
        [CATEGORY]: task?.data?.[CATEGORY]?.toString() || "",
        [SUB_CATEGORY]: task?.data?.[SUB_CATEGORY]?.toString() || "",
        [TASK_NAME]: task?.data?.[TASK_NAME]?.toString() || "",
        [PLANNED_START_DATE]: task?.data?.[PLANNED_START_DATE] ? moment(task?.data?.[PLANNED_START_DATE]).format('MM-DD-YYYY') : "",
        [PLANNED_END_DATE]: task?.data?.[PLANNED_END_DATE] ? moment(task?.data?.[PLANNED_END_DATE]).format('MM-DD-YYYY') : "",
        [SAFETY_CHECK]: task?.data?.[SAFETY_CHECK] || false,
        [QUALITY_CHECK]: task?.data?.[QUALITY_CHECK] || false,
      })
      setDuration(task?.data?.[PLANNED_START_DATE],task?.data?.[PLANNED_END_DATE])
      // Update Redux state with fetched task data to refresh tabs only if values changed
      if (taskData?.requiresSafetyCheck !== (task?.data?.[SAFETY_CHECK] || false) || 
          taskData?.requiresQualityCheck !== (task?.data?.[QUALITY_CHECK] || false)) {
        dispatch(setTaskData({
          ...taskData,
          requiresSafetyCheck: task?.data?.[SAFETY_CHECK] || false,
          requiresQualityCheck: task?.data?.[QUALITY_CHECK] || false,
        }))
      }
    }
  }, [task]);

  const { mutate: addTask, isPending: isAddTaskPending } =
    useAddTaskMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        onClear();
        dispatch(setTaskData(null));
        queryClient.invalidateQueries({queryKey: ["task-list", planId]})
        toast({ message: "Task Info Added Successfully" });
      },
    });

  const { mutate: updateTask, isPending: isUpdateTaskPending } =
    useUpdateTaskMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        // onClear();
        refetchTask();
        queryClient.invalidateQueries({queryKey: ["task-list", planId]})
        toast({ message: "Task Info Updated Successfully" });
      },
    });
    
  const onSubmit = (data: formValues) => {
    const _data = removeEmptyFields(data);

    if (!_data[PLANNED_START_DATE] && !_data[DAYS] && !(_data[DEPS] as any)?.length) {
      toast({
        message: <div>You must do either of below steps for task: <br/>
            1. Fill Planned Start Date & Duration <br/>
            2. Fill Planned Start Date & Planned End Date<br/>
            3. Add Dependency & Duration
          </div>,
        type: 'error'
      })
      return;
    }

    if (taskData?.taskId) {
      updateTask({projectId: id as string, planId: planId as string, 
        id: taskData?.taskId,
        data: {
          ..._data,
          [PLANNED_START_DATE]: _data?.[PLANNED_START_DATE] ? moment((_data as any)?.[PLANNED_START_DATE]).format('YYYY-MM-DDT00:00:00') : "",
          [PLANNED_END_DATE]: _data?.[PLANNED_END_DATE] ? moment((_data as any)?.[PLANNED_END_DATE]).format('YYYY-MM-DDT00:00:00') : ""
      }})
    } else {
      addTask({projectId: id as string, planId: planId as string, 
        data: {
          ..._data,
          [PLANNED_START_DATE]: _data?.[PLANNED_START_DATE] ? moment((_data as any)?.[PLANNED_START_DATE]).format('YYYY-MM-DDT00:00:00') : "",
          [PLANNED_END_DATE]: _data?.[PLANNED_END_DATE] ? moment((_data as any)?.[PLANNED_END_DATE]).format('YYYY-MM-DDT00:00:00') : ""
      }})
    }
  };

  const setDuration = (startDate: any, endDate: any) => {
    if (startDate && endDate) {
      form.setValue(
        DAYS,
        moment(endDate).diff(moment(startDate), "days").toString()
      );
    }
  };

  const {data: dependencyList, isLoading: isLoadingDependency} = useGetDependencyList({
    taskId: taskData?.taskId as string,
    projectId: id as string,
    queryParams: {
      enabled: !!(taskData?.taskId && id)
    }
  })

  useEffect(() => {
    if (JSON.stringify(form.getValues(DEPS)) != JSON.stringify(dependencyList?.data)) {
      form.setValue(DEPS, dependencyList?.data || [])
    }
  }, [dependencyList]);

  const onAddDeps = () => {
    dispatch(setDependencyData(null));
    isEditDeps.current = false;
    setOpenAddEditDepsDialog(true);
  }

  const depsList = useWatch({ control: form.control, name: DEPS });
  
  const onSaveDeps = (data: any) => {
    if (data?.taskDependencyId) {
      const existingData = depsList?.findIndex((val: any) => val.taskDependencyId == data.taskDependencyId);
      const list = [...depsList];
      list[existingData] = {...list[existingData], ...data};
      form.setValue(DEPS, [...list])
    } else {
      const task = taskList?.find((val: any) => data.dependsOnTaskId == val.taskId)
      form.setValue(DEPS, [...depsList, {...data, dependsOnTaskName: task?.taskName || '', taskDependencyId: Math.random() * 2}])
    }
  }

  const onClear = () => {
    form.reset({
      [ID]: "",
      [CATEGORY]: "",
      [SUB_CATEGORY]: "",
      [TASK_NAME]: "",
      [PLANNED_START_DATE]: "",
      [PLANNED_END_DATE]: "",
      [DAYS]: "",
      [SAFETY_CHECK]: false,
      [QUALITY_CHECK]: false,
      [DEPS]: []
    })
  }

  const depsColumns = useMemo(() => {
    return [
      {
        accessorKey: "dependencyType",
        header: "Type",
        accessorFn: (row: any) => String((depsTypes as any)[row?.dependencyType] || ''),
      },
      {
        accessorKey: "lagDays",
        header: <span>{"Lag"}<br/> {"(days)"}</span>,
        accessorFn: (row: any) => String(row?.lagDays || '0'),
      },
      {
        accessorKey: "dependsOnTaskCategoryName",
        header: "Category",
        accessorFn: (row: any) => String(row?.dependsOnTaskCategoryName || ''),
      },
      {
        accessorKey: "dependsOnTaskSubCategoryName",
        header: <span>Sub<br/>Category</span>,
        accessorFn: (row: any) => String(row?.dependsOnTaskSubCategoryName || ''),
        size: 50
      },
      {
        accessorKey: "dependsOnTaskName",
        header: "Task",
        accessorFn: (row: any) => String(row?.dependsOnTaskName || ''),
      },
      ...isView ? [] : [{
        id: "actions",
        header: "Actions",
        cell: ({row}: any) => {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <MoreVertical className="w-5 h-5 cursor-pointer" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-white border-0 shadow-md"
              >
                <DropdownMenuItem
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    isEditDeps.current = true;
                    setOpenAddEditDepsDialog(true);
                    dispatch(setDependencyData(row.original))
                  }}
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpenDeleteDepsDialog(true);
                    dispatch(setDependencyData(row.original))
                    deleteDepsIndex.current = row.index;
                  }}
                >
                  <Trash className="w-4 h-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      }],
    ];
  }, []);

  const { mutate: deleteDependency, isPending: isDeletePending } =
    useDeleteDependencyMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey: ["task-list", planId]})
        queryClient.invalidateQueries({queryKey: ["dependency-list", id, planId]})
        dispatch(setDependencyData(null));
        setOpenDeleteDepsDialog(false);
        toast({ message: "Dependency Deleted Successfully" });
      },
    });
    
  const onConfirmDeleteDeps = () => {
    if (dependencyData?.taskDependencyId) {
      deleteDependency({
        projectId: id as string,
        taskId: dependencyData?.taskId,
        id: dependencyData?.taskDependencyId
      })
    } else {
      dispatch(setDependencyData(null));
      setOpenDeleteDepsDialog(false);
      toast({ message: "Dependency Deleted Successfully" });
      form.setValue(DEPS, [...depsList?.filter((_: any, index: any) => index != deleteDepsIndex.current)])
    }
  }
  
  return {
    taskData,
    form,
    onSubmit,
    requiredFields,
    setDuration,
    isLoadingTask,
    isCategoryListLoading,
    categoryList,
    subCategoryList,
    isSubCategoryListLoading,
    isTaskRefetching,
    planId,
    isView,
    isAddTaskPending,
    isUpdateTaskPending,
    isLoadingDependency,
    onAddDeps,
    openAddEditDepsDialog,
    setOpenAddEditDepsDialog,
    isEditDeps,
    depsList,
    onClear,
    onSaveDeps,
    isRefetchingSubCategories,
    depsColumns,
    openDeleteDepsDialog,
    setOpenDeleteDepsDialog,
    onConfirmDeleteDeps,
    isDeletePending,
    dependencyData,
    isTaskExist
  };
};

export default useTaskPlanning;
